import React, { useState } from 'react';
import { useScoreboard } from './context/ScoreboardContext';
import { useLanguage } from './context/LanguageContext';
import { useDashboardLanguage } from './context/DashboardLanguageContext';

const Settings = () => {
  const {
    showSettings,
    setShowSettings,
    backgroundColor,
    setBackgroundColor,
    defaultGameDuration,
    setDefaultGameDuration,
    defaultOvertime,
    setDefaultOvertime,
    defaultBreakDuration,
    setDefaultBreakDuration,
    overtimeBreak1Duration,
    setOvertimeBreak1Duration,
    overtimeBreak2Duration,
    setOvertimeBreak2Duration,
    autoBreakEnabled,
    setAutoBreakEnabled,
    carouselImages,
    setCarouselImages,
    breakOverlayImages,
    setBreakOverlayImages,
    isBuzzerEnabled,
    setIsBuzzerEnabled,
    buzzerVolume,
    setBuzzerVolume,
    isEntertainmentEnabled,
    activeLicense,
    activateLicense,
    deactivateLicense,
    showTeamInferiority,
    setShowTeamInferiority
  } = useScoreboard();

  const { language, setLanguage, t } = useLanguage();
  const { dashboardLanguage, setDashboardLanguage } = useDashboardLanguage();

  const [activeTab, setActiveTab] = useState('general');
  const [durationInput, setDurationInput] = useState(defaultGameDuration);
  const [overtimeInput, setOvertimeInput] = useState(defaultOvertime);
  const [breakDurationInput, setBreakDurationInput] = useState(defaultBreakDuration);
  const [overtimeBreak1Input, setOvertimeBreak1Input] = useState(overtimeBreak1Duration);
  const [overtimeBreak2Input, setOvertimeBreak2Input] = useState(overtimeBreak2Duration);
  const [nameInput, setNameInput] = useState('');
  const [keyInput, setKeyInput] = useState('');

  if (!showSettings) return null;

  const handleDurationChange = (e) => {
    const value = e.target.value;
    if (value.match(/^\d{0,2}:\d{0,2}$/)) {
      setDurationInput(value);
    }
  };

  const handleDurationBlur = () => {
    const [minutes, seconds] = durationInput.split(':').map(Number);
    if (!isNaN(minutes) && !isNaN(seconds) && seconds < 60) {
      const formattedDuration = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      setDurationInput(formattedDuration);
      setDefaultGameDuration(formattedDuration);
    } else {
      setDurationInput(defaultGameDuration);
    }
  };

  const handleOvertimeChange = (e) => {
    const value = e.target.value;
    if (value.match(/^\d{0,2}:\d{0,2}$/)) {
      setOvertimeInput(value);
    }
  };

  const handleOvertimeBlur = () => {
    const [minutes, seconds] = overtimeInput.split(':').map(Number);
    if (!isNaN(minutes) && !isNaN(seconds) && seconds < 60) {
      const formattedDuration = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      setOvertimeInput(formattedDuration);
      setDefaultOvertime(formattedDuration);
    } else {
      setOvertimeInput(defaultOvertime);
    }
  };

  const handleBreakDurationChange = (e) => {
    const value = e.target.value;
    if (value.match(/^\d{0,2}:\d{0,2}$/)) {
      setBreakDurationInput(value);
    }
  };

  const handleBreakDurationBlur = () => {
    const [minutes, seconds] = breakDurationInput.split(':').map(Number);
    if (!isNaN(minutes) && !isNaN(seconds) && seconds < 60) {
      const formattedDuration = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      setBreakDurationInput(formattedDuration);
      setDefaultBreakDuration(formattedDuration);
    } else {
      setBreakDurationInput(defaultBreakDuration);
    }
  };

  const handleOvertimeBreak1Change = (e) => {
    const value = e.target.value;
    if (value.match(/^\d{0,2}:\d{0,2}$/)) {
      setOvertimeBreak1Input(value);
    }
  };

  const handleOvertimeBreak1Blur = () => {
    const [minutes, seconds] = overtimeBreak1Input.split(':').map(Number);
    if (!isNaN(minutes) && !isNaN(seconds) && seconds < 60) {
      const formattedDuration = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      setOvertimeBreak1Input(formattedDuration);
      setOvertimeBreak1Duration(formattedDuration);
    } else {
      setOvertimeBreak1Input(overtimeBreak1Duration);
    }
  };

  const handleOvertimeBreak2Change = (e) => {
    const value = e.target.value;
    if (value.match(/^\d{0,2}:\d{0,2}$/)) {
      setOvertimeBreak2Input(value);
    }
  };

  const handleOvertimeBreak2Blur = () => {
    const [minutes, seconds] = overtimeBreak2Input.split(':').map(Number);
    if (!isNaN(minutes) && !isNaN(seconds) && seconds < 60) {
      const formattedDuration = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      setOvertimeBreak2Input(formattedDuration);
      setOvertimeBreak2Duration(formattedDuration);
    } else {
      setOvertimeBreak2Input(overtimeBreak2Duration);
    }
  };

  const compressImage = (base64String) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 800;
        const MAX_HEIGHT = 600;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'transparent';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/png', 1.0));
      };
      img.src = base64String;
    });
  };

  const handleImageUpload = (event, index) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const compressedImage = await compressImage(e.target.result);
        const newImages = [...carouselImages];
        newImages[index] = compressedImage;
        setCarouselImages(newImages);
      } catch (error) {
        console.error('Error processing image:', error);
        alert('Error processing image');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleImageRemove = (index) => {
    const newImages = [...carouselImages];
    newImages.splice(index, 1);
    setCarouselImages(newImages);
  };

  const handleBreakImageUpload = (event, index) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const compressedImage = await compressImage(e.target.result);
        const newImages = [...breakOverlayImages];
        newImages[index] = compressedImage;
        setBreakOverlayImages(newImages);
      } catch (error) {
        console.error('Error processing image:', error);
        alert('Error processing image');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleBreakImageRemove = (index) => {
    const newImages = [...breakOverlayImages];
    newImages.splice(index, 1);
    setBreakOverlayImages(newImages);
  };

  const handleActivate = () => {
    const licenseInput = `${nameInput}:${keyInput}`;
    const success = activateLicense(licenseInput);
    if (!success) {
      alert('Invalid license key');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold">{t('settings.title')}</h2>
          <button 
            onClick={() => setShowSettings(false)}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('general')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'general'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('settings.general')}
          </button>
          <button
            onClick={() => setActiveTab('sponsors')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'sponsors'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('settings.sponsors')}
          </button>          
          <button
            onClick={() => setActiveTab('license')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'license'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('settings.license')}
          </button>
          <button
            onClick={() => setActiveTab('beta')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'beta'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Beta Features
          </button>
        </div>
        
        <div className="space-y-8">
          {/* General Settings Tab */}
          {activeTab === 'general' && (
            <>
              {/* Language Settings */}
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('settings.languageSection')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <label className="font-medium w-64">
                      {t('settings.uiLanguage')}:
                    </label>
                    <select
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                      className="px-4 py-2 border rounded text-lg"
                    >
                      <option value="de">{t('languages.de')}</option>
                      <option value="en">{t('languages.en')}</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="font-medium w-64">
                      {t('settings.dashboardLanguage')}:
                    </label>
                    <select
                      value={dashboardLanguage}
                      onChange={(e) => setDashboardLanguage(e.target.value)}
                      className="px-4 py-2 border rounded text-lg"
                    >
                      <option value="de">{t('languages.de')}</option>
                      <option value="en">{t('languages.en')}</option>
                    </select>
                  </div>
                  <p className="text-sm text-gray-600 italic">
                    {t('settings.languageDescription')}
                  </p>
                </div>
              </div>

              {/* Game Timer Settings */}
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('settings.gameDuration')}</h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <div className="flex items-center">
                    <label className="font-medium whitespace-nowrap w-36">{t('settings.defaultPeriod')}:</label>
                    <input
                      type="text"
                      value={durationInput}
                      onChange={handleDurationChange}
                      onBlur={handleDurationBlur}
                      placeholder="MM:SS"
                      className="px-4 py-2 border rounded w-24 text-center text-lg"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="font-medium whitespace-nowrap w-36">{t('settings.overtimePeriod')}:</label>
                    <input
                      type="text"
                      value={overtimeInput}
                      onChange={handleOvertimeChange}
                      onBlur={handleOvertimeBlur}
                      placeholder="MM:SS"
                      className="px-4 py-2 border rounded w-24 text-center text-lg"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="font-medium whitespace-nowrap w-36">{t('settings.breakDuration')}:</label>
                    <input
                      type="text"
                      value={breakDurationInput}
                      onChange={handleBreakDurationChange}
                      onBlur={handleBreakDurationBlur}
                      placeholder="MM:SS"
                      className="px-4 py-2 border rounded w-24 text-center text-lg"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="font-medium whitespace-nowrap w-36">{t('settings.overtimeBreak1')}:</label>
                    <input
                      type="text"
                      value={overtimeBreak1Input}
                      onChange={handleOvertimeBreak1Change}
                      onBlur={handleOvertimeBreak1Blur}
                      placeholder="MM:SS"
                      className="px-4 py-2 border rounded w-24 text-center text-lg"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="font-medium whitespace-nowrap w-36">{t('settings.overtimeBreak2')}:</label>
                    <input
                      type="text"
                      value={overtimeBreak2Input}
                      onChange={handleOvertimeBreak2Change}
                      onBlur={handleOvertimeBreak2Blur}
                      placeholder="MM:SS"
                      className="px-4 py-2 border rounded w-24 text-center text-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Background Color Settings */}
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('settings.brandColor')}</h3>
                <div className="flex items-center">
                  <label className="font-medium whitespace-nowrap w-36">{t('settings.brandColor')}:</label>
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="w-20 h-10"
                  />
                </div>
              </div>

              {/* Sound Settings */}
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('settings.buzzer')}</h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                  <div className="flex items-center">
                    <label className="font-medium whitespace-nowrap w-28 mr-4">{t('settings.buzzerEnabled')}:</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isBuzzerEnabled}
                        onChange={(e) => setIsBuzzerEnabled(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        {isBuzzerEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </label>
                  </div>
                  
                  {isBuzzerEnabled && (
                    <div className="flex items-center">
                      <label className="font-medium whitespace-nowrap w-28 mr-4">{t('settings.buzzerVolume')}:</label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="range"
                          min="0"
                          max="2"
                          step="0.1"
                          value={buzzerVolume}
                          onChange={(e) => setBuzzerVolume(parseFloat(e.target.value))}
                          className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <span className="text-sm font-medium text-gray-700 w-12">
                          {Math.round(buzzerVolume * 100)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Sponsors Tab */}
          {activeTab === 'sponsors' && (
            <div className="space-y-8">
              {/* Carousel Images (Main Scoreboard) */}
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('settings.sponsorsOnScoreboard')}</h3>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">{t('settings.sponsorsDescription')}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[...Array(6)].map((_, index) => {
                      const image = carouselImages[index];
                      return (
                        <div key={index} className="border rounded-lg p-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{t('settings.imageSlot')} {index + 1}</span>
                            {image && (
                              <button
                                onClick={() => handleImageRemove(index)}
                                className="text-red-600 hover:text-red-700 text-sm"
                              >
                                {t('settings.remove')}
                              </button>
                            )}
                          </div>

                          {image ? (
                            <div className="relative w-full h-32">
                              <img
                                src={image}
                                alt={`Carousel ${index + 1}`}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            </div>
                          ) : (
                            <div className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 h-32">
                              <label className="cursor-pointer text-center">
                                <span className="text-sm text-gray-500">{t('settings.clickToUpload')}</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleImageUpload(e, index);
                                    }
                                  }}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Break Overlay Images */}
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('settings.sponsorsOnBreakOverlay')}</h3>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">{t('settings.breakOverlayDescription')}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[...Array(4)].map((_, index) => {
                      const image = breakOverlayImages[index];
                      return (
                        <div key={index} className="border rounded-lg p-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{t('settings.breakImage')} {index + 1}</span>
                            {image && (
                              <button
                                onClick={() => handleBreakImageRemove(index)}
                                className="text-red-600 hover:text-red-700 text-sm"
                              >
                                {t('settings.remove')}
                              </button>
                            )}
                          </div>

                          {image ? (
                            <div className="relative w-full h-32">
                              <img
                                src={image}
                                alt={`Break Overlay ${index + 1}`}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            </div>
                          ) : (
                            <div className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 h-32">
                              <label className="cursor-pointer text-center">
                                <span className="text-sm text-gray-500">{t('settings.clickToUpload')}</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleBreakImageUpload(e, index);
                                    }
                                  }}
                                />
                              </label>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Media License Tab */}
          {activeTab === 'license' && (
            <div>
              <h3 className="text-xl font-semibold mb-4">{t('settings.entertainmentLicense')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.licenseStatus')}
                </label>
                <div className={`text-sm ${isEntertainmentEnabled ? 'text-green-600' : 'text-red-600'}`}>
                  {isEntertainmentEnabled ? `✓ ${t('settings.active')}` : `✗ ${t('settings.inactive')}`}
                </div>
                {activeLicense && (
                  <div className="text-sm text-gray-600 mt-1">
                    {t('settings.licensedTo')}: {activeLicense.split(':')[0]}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {!isEntertainmentEnabled && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('settings.licenseName')}
                      </label>
                      <input
                        type="text"
                        placeholder={t('settings.enterLicenseName')}
                        className="border rounded px-2 py-1 text-sm w-64"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('settings.licenseKey')}
                      </label>
                      <input
                        type="text"
                        placeholder={t('settings.enterLicenseKey')}
                        className="border rounded px-2 py-1 text-sm w-64"
                        value={keyInput}
                        onChange={(e) => setKeyInput(e.target.value)}
                      />
                    </div>

                    <div className="pt-2">
                      <button
                        onClick={handleActivate}
                        className="bg-blue-500 text-white px-4 py-1 rounded text-sm hover:bg-blue-600"
                      >
                        {t('settings.activateLicense')}
                      </button>
                    </div>
                  </>
                )}
                
                {isEntertainmentEnabled && (
                  <div className="pt-2">
                    <button
                      onClick={deactivateLicense}
                      className="bg-red-500 text-white px-4 py-1 rounded text-sm hover:bg-red-600"
                    >
                      {t('settings.deactivateLicense')}
                    </button>
                  </div>
                )}
                </div>
              </div>
            </div>
          )}

          {/* Beta Features Tab */}
          {activeTab === 'beta' && (
            <div>
              <h3 className="text-xl font-semibold mb-4">{t('settings.betaFeatures')}</h3>
              <p className="text-sm text-gray-600 mb-6">{t('settings.betaDescription')}</p>
              
              <div className="space-y-6">
                {/* Auto Break Toggle */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">{t('settings.autoBreak')}</h4>
                    <p className="text-sm text-gray-600 mt-1">{t('settings.autoBreakDescription')}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoBreakEnabled}
                      onChange={(e) => setAutoBreakEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-3 text-sm font-medium text-gray-900">
                      {autoBreakEnabled ? t('settings.enabled') : t('settings.disabled')}
                    </span>
                  </label>
                </div>

                {/* Team Inferiority Display Toggle */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">{t('settings.teamInferiorityDisplay')}</h4>
                    <p className="text-sm text-gray-600 mt-1">{t('settings.teamInferiorityDescription')}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showTeamInferiority}
                      onChange={(e) => setShowTeamInferiority(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-3 text-sm font-medium text-gray-900">
                      {showTeamInferiority ? t('settings.enabled') : t('settings.disabled')}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
